import { useRouter } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";

import { FREE_MESSAGES_OFFER } from "@mandoob/shared";

import { useLanguage } from "@/contexts/language";

const FREE = FREE_MESSAGES_OFFER.freeMessagesPerConversation;
const CAP = FREE_MESSAGES_OFFER.dailyShipmentCap;

/** Five points, kept in sync with FREE_MESSAGES_OFFER by hand. */
const CONTENT: Record<
  "ar" | "en",
  { title: string; period: string; points: string[] }
> = {
  ar: {
    title: "شروط عرض الرسائل المجانية",
    period:
      "من 13 أكتوبر 2026 الساعة 00:00 حتى 20 أكتوبر 2026 الساعة 00:00 بتوقيت مكة المكرمة.",
    points: [
      `أول ${FREE} رسائل ترسلها في كل شحنة (محادثة) مجانية، وتشمل رسالة إشعار الشحنة الأولى.`,
      `ابتداءً من الرسالة رقم ${FREE + 1} في نفس المحادثة تُخصم التكلفة المعتادة (رصيد رسالة واحدة لكل رسالة).`,
      `العرض يشمل حتى ${CAP} شحنة جديدة لكل مندوب في اليوم؛ وما زاد عن ذلك في نفس اليوم يُخصم من رصيدك بالتكلفة المعتادة.`,
      "العرض يسري تلقائيًا على جميع المناديب دون الحاجة إلى رمز، ورصيدك المدفوع يبقى كما هو ولا يُستخدم إلا بعد انتهاء الرسائل المجانية.",
      "ردود العملاء مجانية دائمًا، ويحق لمندوب شات إنهاء العرض أو تعديله في أي وقت.",
    ],
  },
  en: {
    title: "Free messages offer terms",
    period:
      "From 13 October 2026 at 00:00 until 20 October 2026 at 00:00, Makkah time (UTC+3).",
    points: [
      `The first ${FREE} messages you send in each shipment (conversation) are free, the first shipment notification included.`,
      `From message ${FREE + 1} onwards in the same conversation the usual cost applies (1 message credit per message).`,
      `The offer covers up to ${CAP} new shipments per driver per day; any more that day are charged from your credits at the usual cost.`,
      "The offer applies automatically to every driver, no code needed. Your purchased credits stay untouched and are only used once the free messages run out.",
      "Client replies are always free. Mandoob Chat may end or change the offer at any time.",
    ],
  },
};

export function OfferTermsPage() {
  const { t, dir, language } = useLanguage();
  const router = useRouter();
  const content = CONTENT[language];

  return (
    <div dir={dir} className="tw:min-h-dvh tw:bg-background">
      <div className="tw:mx-auto tw:flex tw:w-full tw:max-w-3xl tw:flex-col tw:gap-6 tw:px-6 tw:pb-16 tw:pt-[calc(32px+env(safe-area-inset-top))] tw:md:pt-12">
        <header className="tw:flex tw:flex-col tw:gap-3">
          <button
            type="button"
            onClick={() => router.history.back()}
            className="tw:inline-flex tw:w-fit tw:cursor-pointer tw:items-center tw:gap-1.5 tw:border-0 tw:bg-transparent tw:p-0 tw:text-sm tw:font-medium tw:text-brand tw:hover:underline"
          >
            <ArrowRight className="tw:size-4 tw:ltr:-scale-x-100" />
            {t("Back")}
          </button>
          <h1 className="tw:m-0 tw:text-3xl tw:font-bold tw:text-foreground">
            {content.title}
          </h1>
          <p className="tw:m-0 tw:text-sm tw:text-muted-foreground">
            {content.period}
          </p>
        </header>

        <img
          src="/mandoob-chat-free-campaign.png"
          alt={content.title}
          className="tw:aspect-2/1 tw:w-full tw:rounded-2xl tw:object-cover"
        />

        <ol className="tw:m-0 tw:flex tw:list-decimal tw:flex-col tw:gap-3 tw:ps-5">
          {content.points.map((point) => (
            <li
              key={point}
              className="tw:text-base tw:leading-relaxed tw:text-foreground"
            >
              {point}
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}
